import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  SearchableSelectDropdown,
  SearchableSelectOption,
} from './styles';
import {
  InputContainer,
  Label as InputLabel,
  InputWrapper,
  StyledInput,
} from '../../atoms/Input/styles';

export interface SearchableSelectProps<T = string> {
  options: T[];
  /** Fires when a dropdown suggestion is picked, in both modes below. */
  onChange: (value: T) => void;
  placeholder?: string;
  /** Text shown for an option, both in the closed input (classic mode) and
   * (by default) in each dropdown row. Defaults to treating T as a string. */
  getOptionLabel?: (option: T) => string;
  /** Unique key per option, for React lists and the "selected" highlight.
   * Defaults to getOptionLabel(option). */
  getOptionKey?: (option: T) => string | number;
  /** Custom row rendering — e.g. a flag + name + dial code. Defaults to
   * plain getOptionLabel(option) text. */
  renderOption?: (option: T) => React.ReactNode;
  /** Custom local filter predicate. Defaults to a case-insensitive
   * substring match against getOptionLabel(option). Ignored when
   * filterLocally is false. */
  filterOption?: (option: T, term: string) => boolean;
  /** Set to false when `options` is already filtered by the caller (e.g. a
   * debounced server-side search) — skips the built-in local filtering so
   * a match on a field other than the label (email, dial code, etc.)
   * doesn't get hidden by a stale re-filter. Defaults to true. */
  filterLocally?: boolean;
  /** Fires on every keystroke — the hook for callers doing async/server-side
   * filtering (combine with filterLocally=false). In freeform mode this
   * fires with the same text as onInputValueChange. */
  onSearchTermChange?: (term: string) => void;
  /** Optional label rendered above the input, matching the shared Input
   * atom's default label style — lets this drop into a labeled form field
   * instead of only a compact filter bar. */
  label?: string;
  required?: boolean;
  emptyMessage?: string;
  /** Freeform mode only: don't render the dropdown at all (not even the
   * empty-message state) until inputValue reaches this length — avoids
   * showing "No results" the instant an empty field is focused, before the
   * caller's async search has anything to say. Ignored in classic mode,
   * where focusing has always shown the full option list. Defaults to 0. */
  minSearchLength?: number;

  /** Classic select mode: value must be one of `options` (or the literal
   * "all"); the closed-state input shows getOptionLabel(value). This is the
   * original behavior — a rich <select> replacement. */
  value?: T;

  /** Freeform mode: pass inputValue/onInputValueChange instead of value for
   * fields where the text can be anything, not just an option's own label
   * (e.g. "type a company name, optionally pick a known match"). Clicking a
   * suggestion still calls onChange(option) so the caller can autofill
   * related fields, but never forces the input's own text — the caller owns
   * inputValue completely. */
  inputValue?: string;
  onInputValueChange?: (text: string) => void;
}

const defaultGetOptionLabel = (option: unknown): string =>
  typeof option === 'string' ? option : String(option);

export function SearchableSelect<T = string>({
  options,
  onChange,
  placeholder = 'Search...',
  getOptionLabel = defaultGetOptionLabel as (option: T) => string,
  getOptionKey,
  renderOption,
  filterOption,
  filterLocally = true,
  onSearchTermChange,
  label,
  required,
  emptyMessage = 'No results',
  minSearchLength = 0,
  value,
  inputValue,
  onInputValueChange,
}: SearchableSelectProps<T>) {
  const isFreeform = inputValue !== undefined;
  const [isOpen, setIsOpen] = useState(false);
  const [classicSearchTerm, setClassicSearchTerm] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  const resolveKey = getOptionKey ?? ((option: T) => getOptionLabel(option));
  const matchesTerm =
    filterOption ?? ((option: T, term: string) => getOptionLabel(option).toLowerCase().includes(term.toLowerCase()));

  const activeTerm = isFreeform ? inputValue : classicSearchTerm;

  const filteredOptions = useMemo(() => {
    if (!filterLocally || !activeTerm) return options;
    return options.filter((opt) => matchesTerm(opt, activeTerm));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options, activeTerm, filterLocally]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const displayValue = isFreeform
    ? inputValue
    : isOpen
      ? classicSearchTerm
      : value === 'all'
        ? 'All'
        : getOptionLabel(value as T);

  return (
    <InputContainer ref={containerRef}>
      {label && <InputLabel>{label}{required && ' *'}</InputLabel>}
      <InputWrapper $variant="default" $size="default">
        <StyledInput
          $size="default"
          type="text"
          placeholder={placeholder}
          value={displayValue}
          onChange={(e) => {
            if (isFreeform) {
              onInputValueChange?.(e.target.value);
            } else {
              setClassicSearchTerm(e.target.value);
            }
            onSearchTermChange?.(e.target.value);
          }}
          onFocus={() => setIsOpen(true)}
          autoComplete="off"
        />
      </InputWrapper>
      {isOpen && (!isFreeform || inputValue.trim().length >= minSearchLength) && (
        <SearchableSelectDropdown>
          {filteredOptions.length === 0 ? (
            <SearchableSelectOption $selected={false} style={{ color: '#9ca3af' }}>
              {emptyMessage}
            </SearchableSelectOption>
          ) : (
            filteredOptions.map((opt) => (
              <SearchableSelectOption
                key={resolveKey(opt)}
                $selected={!isFreeform && value !== undefined && resolveKey(value) === resolveKey(opt)}
                onMouseDown={(e) => {
                  // preventDefault so this fires before the input's blur
                  // handler would otherwise close the dropdown first.
                  e.preventDefault();
                  onChange(opt);
                  setIsOpen(false);
                  if (!isFreeform) setClassicSearchTerm('');
                }}
              >
                {renderOption ? renderOption(opt) : getOptionLabel(opt)}
              </SearchableSelectOption>
            ))
          )}
        </SearchableSelectDropdown>
      )}
    </InputContainer>
  );
}
